import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { ErrorCode, type PermissionKey, type PermissionSet } from '@brimo/shared';
import { ApiError, UNAUTHENTICATED_EVENT, api } from './api';
import { LoadErrorPage } from '../shell/LoadErrorPage';

/**
 * Session state of the SPA (docs/dev/api-shell.md `SessionInfo`). One query (`['session']`) feeds the
 * routing guards, the topbar and the permission hooks; SSE events and mutations invalidate it.
 */

export type ToolKey = 'dialer' | 'tickets' | 'deals' | 'financing' | 'pkv';
export type ToolState = 'ok' | 'module_off' | 'no_permission';

export interface SessionWorkspace {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  userId: string;
  roleKey: string;
  /** External company of the own membership there (0037); null for an internal member. */
  externalCompany: string | null;
  /** Name of the parent organization that services this membership (0045), or null for an own membership. */
  servicingFrom: string | null;
  active: boolean;
}

export interface SessionUser {
  id: string;
  firstName: string;
  lastName: string;
  initials: string;
  roleKey: string;
  roleLabel: string;
  avatarUrl: string | null;
}

export interface ActiveWorkspace {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  user: SessionUser;
  permissions: PermissionSet;
  modules: string[];
  tools: Record<ToolKey, ToolState>;
  taskCount: number;
  queuePaused: boolean;
  unreadNotifications: number;
  onboardingDone: boolean;
  /** M8: which checklists the app shows (Nutzer 13–15, Admin 11). */
  onboarding: { userChecklistVisible: boolean; organizationChecklistVisible: boolean };
  /** "A. Brandl" — the organization's approval recipient named on the locked pages, or null. */
  approvalRecipientName: string | null;
}

/** M8: a setup session (onboarding step `security` open; with required 2FA also no 2FA yet) runs its wizard first. */
export interface SessionSetup {
  kind: 'organization' | 'user';
  organizationSlug: string;
  userId: string;
}

export interface SessionInfo {
  account: { id: string; email: string; twoFactorEnabled: boolean };
  twoFactorPassed: boolean;
  /** `TWO_FACTOR_MODE` of the server (D-338): false = login without 2FA, the 2FA screens are never required. */
  twoFactorRequired: boolean;
  setup: SessionSetup | null;
  workspaces: SessionWorkspace[];
  active: ActiveWorkspace | null;
}

export const sessionQueryKey = ['session'] as const;

export function fetchSession(): Promise<SessionInfo> {
  return api<SessionInfo>('/auth/session');
}

/** The session query; a 401 means "not signed in" and is not retried. */
export function useSessionQuery(): UseQueryResult<SessionInfo, Error> {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: fetchSession,
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 1,
    staleTime: 60_000,
  });
}

export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401 && error.code === ErrorCode.UNAUTHENTICATED;
}

/** Entry of the wizard of a setup session; the entry route resumes at the wizard's current step. */
export function setupRouteFor(setup: SessionSetup): string {
  return setup.kind === 'organization' ? '/setup/organization' : '/setup/user';
}

/**
 * Where a signed-in session has to go next: the wizard of a setup session, the 2FA screens (only while the server
 * requires 2FA), or the app.
 */
export function nextRouteFor(session: SessionInfo): string | null {
  if (session.setup) return setupRouteFor(session.setup);
  if (session.twoFactorRequired && !session.twoFactorPassed) {
    return session.account.twoFactorEnabled ? '/login/2fa' : '/login/2fa/setup';
  }
  return null;
}

const SessionContext = createContext<SessionInfo | null>(null);

/**
 * Makes the loaded session available below (`useSession`, `useWorkspace`, `usePermissions`) and turns
 * a 401 from any request into a fresh session check, so an expired session lands on the login page.
 */
export function SessionProvider({ session, children }: { session: SessionInfo; children: ReactNode }) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const onUnauthenticated = () => {
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onUnauthenticated);
  }, [queryClient]);
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionInfo {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('useSession() needs a <SessionProvider>');
  return session;
}

/** The active workspace; only rendered below `RequireAuth`, which guarantees one. */
export function useWorkspace(): ActiveWorkspace {
  const session = useSession();
  if (session.active === null) throw new Error('useWorkspace() outside an active workspace');
  return session.active;
}

export interface Permissions {
  set: PermissionSet;
  /** True when the permission has any value other than `no`/`none`. */
  has(key: PermissionKey): boolean;
  /** True when the permission has exactly this value. */
  is(key: PermissionKey, value: string): boolean;
  tool(tool: ToolKey): ToolState;
}

export function usePermissions(): Permissions {
  const workspace = useWorkspace();
  return useMemo(
    () => ({
      set: workspace.permissions,
      has: (key) => {
        const value = workspace.permissions[key] as string;
        return value !== 'no' && value !== 'none';
      },
      is: (key, value) => (workspace.permissions[key] as string) === value,
      tool: (tool) => workspace.tools[tool],
    }),
    [workspace],
  );
}

/** Blocks the workspace routes until the session is loaded and complete (login → 2FA → workspace). */
export function RequireAuth({ fallback, children }: { fallback: ReactNode; children?: ReactNode }) {
  const location = useLocation();
  const query = useSessionQuery();

  if (query.isPending) return <>{fallback}</>;
  if (query.isError) {
    if (isUnauthenticated(query.error)) {
      return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
    }
    return <LoadErrorPage error={query.error} onRetry={() => void query.refetch()} />;
  }
  const session = query.data;
  const next = nextRouteFor(session);
  if (next !== null) return <Navigate to={next} replace />;
  return <SessionProvider session={session}>{children ?? <Outlet />}</SessionProvider>;
}
